import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Card,
  CardContent,

  Chip,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Divider,
  Tabs,
  Tab
} from '@mui/material';

import { Upload, CloudUpload, CheckCircle, Schedule, Assignment, FileDownload, AccessTime, TaskAlt, WarningAmber, TrendingUp } from '@mui/icons-material';
import ValidadoLogo from '../components/ValidadoLogo';

// DiDi client credentials
const validClientCredentials = {
  'viviansantanna@didiglobal.com': '4a5bTQ4I36f8',
  'mcoyramirez@didiglobal.com': '75dM9MPl1Wcf',
  'viviansantanna@99app.com': '4a5bTQ4I36f8',
  'castillo.andres_v@didiglobal.com': '#Ii1_~#7AUpNwJu3T',
  'gabrielwang@didiglobal.com': 'F2pkKT8ikGeuebKz'
};

interface DidiRecord {
  id: string;
  nationalId: string;
  status: string;
  uploadDate: string;
  lastUpdated: string;
  completionDate?: string;
  result?: string;
  uploadedBy?: string;
  uploadedByName?: string;
  uploadedByEmail?: string;
}

interface DidiUploaderOption {
  email: string;
  name: string;
}

interface DidiPortalPageProps {
  autoLoginEmail?: string;
}

interface DidiPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface DidiDashboardMetrics {
  total: number;
  inQueue: number;
  filteredCompleted: number;
  clearCount: number;
  notClearCount: number;
  averageHours: number;
  medianHours: number;
  fastestHours: number;
  slowestHours: number;
  completionRate: number;
  clearRate: number;
  resolvedLast7Days: number;
}

const DidiPortalPage: React.FC<DidiPortalPageProps> = ({ autoLoginEmail }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [yearFilter, setYearFilter] = useState('all');
  const [resultFilter, setResultFilter] = useState('all');
  const [ownershipScope, setOwnershipScope] = useState('all');
  const [uploaderFilter, setUploaderFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState<DidiRecord[]>([]);
  const [availableUploaders, setAvailableUploaders] = useState<DidiUploaderOption[]>([]);
  const [pagination, setPagination] = useState<DidiPagination>({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [dashboardMetrics, setDashboardMetrics] = useState<DidiDashboardMetrics>({
    total: 0,
    inQueue: 0,
    filteredCompleted: 0,
    clearCount: 0,
    notClearCount: 0,
    averageHours: 0,
    medianHours: 0,
    fastestHours: 0,
    slowestHours: 0,
    completionRate: 0,
    clearRate: 0,
    resolvedLast7Days: 0
  });
  const [stats, setStats] = useState({
    total: 0,
    success_upload: 0,
    under_review: 0,
    passed_to_manual: 0,
    completed: 0
  });
  const companyUploaders = availableUploaders.filter((uploader) => uploader.email !== email);

  // Auto-refresh records every 5 seconds
 useEffect(() => {
    if (autoLoginEmail) {
      setEmail(autoLoginEmail);
      setIsAuthenticated(true);
    }
  }, [autoLoginEmail]);

 useEffect(() => {
    if (isAuthenticated) {
      fetchRecords();
      const interval = setInterval(fetchRecords, 10000);
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, yearFilter, resultFilter, ownershipScope, uploaderFilter, page]);

  const handleLogin = () => {
    const validPassword = validClientCredentials[email as keyof typeof validClientCredentials];
    
    if (validPassword && validPassword === password) {
      setIsAuthenticated(true);
      setPage(1);
      setError('');
      fetchRecords();
    } else {
      setError('Invalid credentials. Please check your email and password.');
    }
  };

  const fetchRecords = async () => {
    try {
      const response = await fetch(`/api/didi/records?clientEmail=${encodeURIComponent(email)}&year=${yearFilter}&result=${resultFilter}&ownership=${ownershipScope}&uploader=${encodeURIComponent(uploaderFilter)}&page=${page}&limit=50`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecords(data.data.records || []);
        setPagination(data.data.pagination || { page: 1, limit: 50, total: 0, pages: 0 });
        setAvailableUploaders(data.data.availableUploaders || []);
        setDashboardMetrics(data.data.dashboardMetrics || {
          total: 0,
          inQueue: 0,
          filteredCompleted: 0,
          clearCount: 0,
          notClearCount: 0,
          averageHours: 0,
          medianHours: 0,
          fastestHours: 0,
          slowestHours: 0,
          completionRate: 0,
          clearRate: 0,
          resolvedLast7Days: 0
        });
        updateStats(data.data.records || [], data.data.statusCounts);
      }
    } catch (error) {
      console.error('Failed to fetch records:', error);
    }
  };

  const formatDuration = (hours: number) => {
    if (!Number.isFinite(hours) || hours <= 0) {
      return 'N/A';
    }

    if (hours < 24) {
      return `${hours.toFixed(1)}h`;
    }

    return `${(hours / 24).toFixed(1)}d`;
  };

  const updateStats = (recordsList: DidiRecord[], statusCounts: any) => {
    // If statusCounts is provided by backend, use it, otherwise calculate locally
    if (statusCounts) {
      // Merge manual_completed into completed for display purposes
      const mergedStats = {
        ...statusCounts,
        completed: (statusCounts.completed || 0) + (statusCounts.manual_completed || 0)
      };
      setStats(mergedStats);
      return;
    }

    const newStats = {
      total: recordsList.length,
      success_upload: recordsList.filter(r => r.status === 'success_upload').length,
      under_review: recordsList.filter(r => r.status === 'under_review').length,
      passed_to_manual: recordsList.filter(r => r.status === 'passed_to_manual').length,
      completed: recordsList.filter(r => r.status === 'completed' || r.status === 'manual_completed').length
    };
    setStats(newStats);
  };

  const handleFileUpload = async () => {
    if (!csvFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('csvFile', csvFile);
    formData.append('clientEmail', email);
    formData.append('companyName', 'DiDi');

    try {
      const response = await fetch('/api/didi/upload-csv', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setCsvFile(null);
        fetchRecords();
      } else {
        setError('Upload failed. Please try again.');
      }
    } catch (error) {
      setError('Upload failed. Please check your connection.');
    } finally {
      setUploading(false);
    }
  };

  const handleExport = () => {
    window.location.href = `/api/didi/export?clientEmail=${encodeURIComponent(email)}&year=${yearFilter}&result=${resultFilter}&ownership=${ownershipScope}&uploader=${encodeURIComponent(uploaderFilter)}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success_upload': return 'success';
      case 'under_review': return 'warning';
      case 'passed_to_manual': return 'info';
      case 'completed': return 'primary';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement => {
    switch (status) {
      case 'success_upload': return <CloudUpload />;
      case 'under_review': return <Schedule />;
      case 'passed_to_manual': return <Assignment />;
      case 'completed': return <CheckCircle />;
      default: return <Assignment />;
    }
  };

  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <ValidadoLogo width={200} height={80} />
            <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 2 }}>
              DiDi Background Check Portal
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center">
              Please login to access your temporary background check portal
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            margin="normal"
            required
          />
          
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            margin="normal"
            required
          />
          
          <Button
            fullWidth
            variant="contained"
            onClick={handleLogin}
            sx={{ mt: 3, mb: 2 }}
            size="large"
          >
            Login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <ValidadoLogo width={150} height={60} />
          <Box ml={2}>
            <Typography variant="h4" component="h1">
              DiDi Background Check Portal
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Welcome, {email}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownload />}
            onClick={handleExport}
            sx={{ mr: 2 }}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            onClick={() => setIsAuthenticated(false)}
          >
            Logout
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 3, p: 1 }}>
        <Tabs
          value={ownershipScope}
          onChange={(_event, value) => {
            setOwnershipScope(value);
            setUploaderFilter('all');
            setPage(1);
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="all" label="All Uploads" />
          <Tab value="mine" label="My Uploads" />
          <Tab value="company" label="Company Uploads" />
        </Tabs>
      </Paper>

      {companyUploaders.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={(
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setOwnershipScope('company');
                setUploaderFilter('all');
                setPage(1);
              }}
            >
              Show Company Uploads
            </Button>
          )}
        >
          Company uploads are available from {companyUploaders.map((uploader) => uploader.name).join(', ')}.
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Background Check Intelligence
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Executive view of uploaded checks, final outcomes, and processing speed for the current filters.
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2, mb: 4 }}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)', color: 'white' }}>
            <CardContent>
              <Typography sx={{ opacity: 0.8 }} gutterBottom>Total Submissions</Typography>
              <Typography variant="h4">{dashboardMetrics.total}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                Visible in current view
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>Pending or In Progress</Typography>
                  <Typography variant="h4" color="primary.main">{dashboardMetrics.inQueue}</Typography>
                </Box>
                <Schedule color="primary" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Active operational queue
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>Completed</Typography>
                  <Typography variant="h4" color="success.main">{dashboardMetrics.filteredCompleted}</Typography>
                </Box>
                <TaskAlt color="success" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {dashboardMetrics.completionRate.toFixed(1)}% completion rate
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>Clear</Typography>
                  <Typography variant="h4" color="success.main">{dashboardMetrics.clearCount}</Typography>
                </Box>
                <CheckCircle color="success" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Positive final decisions
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>Not Clear</Typography>
                  <Typography variant="h4" color="error.main">{dashboardMetrics.notClearCount}</Typography>
                </Box>
                <WarningAmber color="error" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Flagged final decisions
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1.4fr) minmax(320px, 1fr)', gap: 2, mb: 4 }}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom>
              Result Mix
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Final outcomes for completed checks in the current view.
            </Typography>

            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2">Clear</Typography>
                <Typography variant="body2" fontWeight={600}>{dashboardMetrics.clearCount}</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={dashboardMetrics.filteredCompleted ? (dashboardMetrics.clearCount / dashboardMetrics.filteredCompleted) * 100 : 0}
                sx={{ height: 10, borderRadius: 10, backgroundColor: 'rgba(25, 118, 210, 0.08)', '& .MuiLinearProgress-bar': { backgroundColor: '#2e7d32' } }}
              />
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2">Not Clear</Typography>
                <Typography variant="body2" fontWeight={600}>{dashboardMetrics.notClearCount}</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={dashboardMetrics.filteredCompleted ? (dashboardMetrics.notClearCount / dashboardMetrics.filteredCompleted) * 100 : 0}
                sx={{ height: 10, borderRadius: 10, backgroundColor: 'rgba(25, 118, 210, 0.08)', '& .MuiLinearProgress-bar': { backgroundColor: '#d32f2f' } }}
              />
            </Box>

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2">Decided Checks</Typography>
                <Typography variant="body2" fontWeight={600}>{dashboardMetrics.clearCount + dashboardMetrics.notClearCount}</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={dashboardMetrics.filteredCompleted ? ((dashboardMetrics.clearCount + dashboardMetrics.notClearCount) / dashboardMetrics.filteredCompleted) * 100 : 0}
                sx={{ height: 10, borderRadius: 10, backgroundColor: 'rgba(25, 118, 210, 0.08)', '& .MuiLinearProgress-bar': { backgroundColor: '#1d4ed8' } }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Clear Share</Typography>
                <Typography variant="h5">{dashboardMetrics.clearRate.toFixed(1)}%</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Resolved Last 7 Days</Typography>
                <Typography variant="h5">{dashboardMetrics.resolvedLast7Days}</Typography>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom>
              Processing Insights
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Based on upload date versus completion date for completed checks.
            </Typography>

            <Box sx={{ display: 'grid', gap: 2 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Average Processing Time</Typography>
                    <Typography variant="h5">{formatDuration(dashboardMetrics.averageHours)}</Typography>
                  </Box>
                  <AccessTime color="primary" />
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Median Processing Time</Typography>
                    <Typography variant="h5">{formatDuration(dashboardMetrics.medianHours)}</Typography>
                  </Box>
                  <TrendingUp color="action" />
                </CardContent>
              </Card>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>Fastest</Typography>
                    <Typography variant="h6">{formatDuration(dashboardMetrics.fastestHours)}</Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>Slowest</Typography>
                    <Typography variant="h6">{formatDuration(dashboardMetrics.slowestHours)}</Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 4 }}>
          <Paper sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">API Total</Typography>
            <Typography variant="h5">{stats.total}</Typography>
          </Paper>
          <Paper sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">Under Review</Typography>
            <Typography variant="h5">{stats.under_review}</Typography>
          </Paper>
          <Paper sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">Manual Review</Typography>
            <Typography variant="h5">{stats.passed_to_manual}</Typography>
          </Paper>
          <Paper sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">Current Queue</Typography>
            <Typography variant="h5">{dashboardMetrics.inQueue}</Typography>
          </Paper>
        </Box>
      </Box>

      {/* Upload Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Upload CSV File
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
            style={{ display: 'none' }}
            id="csv-upload"
          />
          <label htmlFor="csv-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<Upload />}
            >
              Choose CSV File
            </Button>
          </label>
          {csvFile && (
            <Typography variant="body2">
              {csvFile.name}
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleFileUpload}
            disabled={!csvFile || uploading}
            startIcon={<CloudUpload />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Box>
        {uploading && <LinearProgress sx={{ mt: 2 }} />}
      </Paper>

      {/* Filters */}
      <Paper sx={{ mb: 3, p: 2, bgcolor: 'background.paper' }}>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={yearFilter}
              label="Year"
              onChange={(e: any) => {
                setYearFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">All Years</MenuItem>
              <MenuItem value="2026">2026</MenuItem>
              <MenuItem value="2025">2025</MenuItem>
              <MenuItem value="2024">2024</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Result</InputLabel>
            <Select
              value={resultFilter}
              label="Result"
              onChange={(e: any) => {
                setResultFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">All Results</MenuItem>
              <MenuItem value="Clear">Clear</MenuItem>
              <MenuItem value="Not Clear">Not Clear</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Uploaded By</InputLabel>
            <Select
              value={uploaderFilter}
              label="Uploaded By"
              onChange={(e: any) => {
                setUploaderFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">All Uploaders</MenuItem>
              {availableUploaders.map((uploader) => (
                <MenuItem key={uploader.email} value={uploader.email}>
                  {uploader.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          `Company Uploads` shows records uploaded by other people in your visible DiDi scope.
        </Typography>
      </Paper>

      {/* Records Table */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Background Check Records
        </Typography>
        
        {/* Table Header */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          p={2} 
          borderBottom={1} 
          borderColor="grey.300"
          bgcolor="grey.100"
          mb={1}
        >
          <Box flex={2}>
            <Typography variant="subtitle2" fontWeight="bold">National ID</Typography>
          </Box>
          <Box flex={2}>
            <Typography variant="subtitle2" fontWeight="bold">Dates</Typography>
          </Box>
          <Box flex={2}>
            <Typography variant="subtitle2" fontWeight="bold">Uploaded By</Typography>
          </Box>
          <Box flex={2}>
            <Typography variant="subtitle2" fontWeight="bold">Status & Result</Typography>
          </Box>
        </Box>

        {records.length === 0 ? (
          <Typography color="text.secondary" p={2} align="center">
            No records found. Upload a CSV file to get started.
          </Typography>
        ) : (
          <Box>
            {records.map((record) => (
              <Box
                key={record.id}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={2}
                borderBottom={1}
                borderColor="grey.200"
                sx={{ '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Box flex={2}>
                  <Typography variant="body1" fontWeight="bold">
                    {record.nationalId}
                  </Typography>
                </Box>
                
                <Box flex={2}>
                  <Typography variant="body2" color="text.secondary">
                    Uploaded: {new Date(record.uploadDate).toLocaleDateString()}
                  </Typography>
                  {record.completionDate && (
                    <Typography variant="body2" color="text.primary" fontWeight="medium">
                      Last Updated: {new Date(record.completionDate).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>

                <Box flex={2}>
                  <Typography variant="body2" fontWeight="medium">
                    {record.uploadedByName || record.uploadedBy || '-'}
                  </Typography>
                  {record.uploadedByEmail && (
                    <Typography variant="body2" color="text.secondary">
                      {record.uploadedByEmail}
                    </Typography>
                  )}
                </Box>

                <Box flex={2} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Chip
                    icon={getStatusIcon(record.status)}
                    label={record.status.replace('_', ' ').toUpperCase()}
                    color={getStatusColor(record.status) as any}
                    variant="outlined"
                    size="small"
                  />
                  {record.result && record.result !== 'Pending' && (
                    <Chip 
                      label={record.result} 
                      color={record.result === 'Clear' ? 'success' : record.result === 'Not Clear' ? 'error' : 'default'} 
                      variant="filled"
                      size="small"
                    />
                  )}
                </Box>
              </Box>
            ))}
            <Box mt={3} display="flex" flexDirection="column" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Showing page {pagination.page} of {pagination.pages || 1} ({pagination.total} total records)
              </Typography>
              <Pagination
                page={pagination.page}
                count={pagination.pages || 1}
                color="primary"
                onChange={(_event, value) => setPage(value)}
              />
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default DidiPortalPage;
